import React, { useRef } from 'react';
import { FileSignature, Image as ImageIcon, X } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import { toast } from '../../hooks/use-toast';

/**
 * Signature editor — RichText + image upload + width slider + preview.
 * Extracted from EmailMailboxSection (Vague 97.3).
 *
 * Encapsulates its own file input ref + image validation toasts to keep the
 * orchestrator free of DOM concerns.
 */
const MAX_BYTES = 300 * 1024;
const ALLOWED_MIME = /^image\/(png|jpeg|jpg|gif)$/i;

export default function EmailSignatureEditor({ form, setForm, signaturePreview, setSignaturePreview }) {
  const fileInputRef = useRef(null);

  const handleImagePick = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast({ title: 'Image trop volumineuse', description: 'Max 300 Ko pour garantir la délivrabilité des emails' });
      return;
    }
    if (!ALLOWED_MIME.test(file.type)) {
      toast({ title: 'Format non supporté', description: 'Utilisez PNG, JPEG ou GIF' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setSignaturePreview(dataUrl);
      setForm((s) => ({
        ...s,
        signature_image_b64: dataUrl, // backend strips the prefix
        signature_image_mime: file.type,
        signature_image_set: true,
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeSignatureImage = () => {
    setSignaturePreview(null);
    setForm((s) => ({ ...s, signature_image_b64: '', signature_image_mime: '', signature_image_set: false }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const plainSignatureLength = (form.signature_html || '').replace(/<[^>]+>/g, '').trim().length;

  return (
    <div className="mt-5 pt-4 border-t border-[#EFF0F6]">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
          <FileSignature className="w-4 h-4 text-[#4F46E5]" />
        </div>
        <div>
          <h3 className="text-[13.5px] font-semibold text-[#1A1B2E]">Signature email</h3>
          <p className="text-[11px] text-[#8E8FA5]">Ajoutée automatiquement en bas de tous vos emails (devis, factures, contrats, relances).</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Text editor */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[12.5px] font-semibold text-[#4A4B63]">Éditeur de signature</label>
            {plainSignatureLength > 0 ? (
              <span data-testid="sig-html-saved-badge" className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#D1FAE5] text-[#047857]">
                ✓ Saisi · {plainSignatureLength} caractères
              </span>
            ) : (
              <span data-testid="sig-html-empty-badge" className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">
                ⚠ Vide — saisissez votre texte
              </span>
            )}
          </div>
          <RichTextEditor
            testId="email-signature-editor"
            value={form.signature_html}
            placeholder="(exemple) Marie Dupont — Directrice commerciale — OFManager"
            onChange={(html) => setForm((s) => ({ ...s, signature_html: html }))}
          />
          <div className="text-[10.5px] text-[#8E8FA5] mt-1">
            Le texte gris ci-dessus est un <strong>exemple</strong>, pas votre signature. Saisissez votre vrai texte.
          </div>
        </div>

        {/* Image upload */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[12.5px] font-semibold text-[#4A4B63]">Image (logo, signature manuscrite…)</label>
            {form.signature_image_set ? (
              <span data-testid="sig-img-saved-badge" className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#D1FAE5] text-[#047857]">
                ✓ Image chargée
              </span>
            ) : (
              <span data-testid="sig-img-empty-badge" className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#6B6C85]">
                Aucune image
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif"
            data-testid="email-signature-image-input"
            onChange={(e) => handleImagePick(e.target.files?.[0])}
            className="hidden"
          />
          {signaturePreview || form.signature_image_set ? (
            <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3 space-y-3">
              <div className="min-h-[110px] flex items-center justify-center bg-white rounded-lg p-2 border border-[#F3F4F8] overflow-hidden">
                {signaturePreview && (
                  <img
                    src={signaturePreview}
                    alt="signature"
                    style={{ width: `${form.signature_image_width}px`, maxWidth: '100%', height: 'auto' }}
                  />
                )}
              </div>
              {/* Width slider */}
              <div>
                <div className="flex items-center justify-between text-[11.5px] font-semibold text-[#4A4B63] mb-1">
                  <span>Largeur d'affichage</span>
                  <span className="text-[#4F46E5]">{form.signature_image_width}px</span>
                </div>
                <input
                  type="range"
                  min="80"
                  max="600"
                  step="10"
                  value={form.signature_image_width}
                  onChange={(e) => setForm((s) => ({ ...s, signature_image_width: parseInt(e.target.value, 10) }))}
                  data-testid="email-signature-image-width"
                  className="w-full accent-[#4F46E5]"
                />
                <div className="flex justify-between text-[10px] text-[#8E8FA5] mt-0.5">
                  <span>80px (compact)</span>
                  <span>600px (large)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="email-signature-image-change"
                  className="h-7 px-2 rounded-lg bg-white border border-[#EFF0F6] text-[11.5px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]"
                >
                  Changer
                </button>
                <button
                  type="button"
                  onClick={removeSignatureImage}
                  data-testid="email-signature-image-remove"
                  className="h-7 px-2 rounded-lg text-[#B91C1C] text-[11.5px] font-semibold flex items-center gap-1 hover:bg-[#FEF2F2]"
                >
                  <X className="w-3 h-3" /> Retirer
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="email-signature-image-upload"
              className="w-full min-h-[152px] rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] hover:bg-[#EEF2FF] flex flex-col items-center justify-center gap-2 text-[#4F46E5] transition-colors"
            >
              <ImageIcon className="w-6 h-6" />
              <div className="text-[12.5px] font-semibold">Déposer ou choisir une image</div>
              <div className="text-[10.5px] text-[#8E8FA5]">PNG · JPEG · GIF — max 300 Ko</div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
